import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  BriefcaseIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';

const statusBadgeClasses = {
  new_lead: 'bg-indigo-100 text-indigo-800',
  unable_to_send_message: 'bg-amber-100 text-amber-800',
  initial_msg_sent: 'bg-blue-100 text-blue-800',
  interviewed: 'bg-yellow-100 text-yellow-800',
  offered: 'bg-purple-100 text-purple-800',
  hired: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  on_hold: 'bg-gray-100 text-gray-800'
};

const formatStatus = (status) => String(status || '').replaceAll('_', ' ');

const formatChangedFields = (changedFields) => {
  if (!changedFields || typeof changedFields !== 'object') {
    return [];
  }

  return Object.entries(changedFields).map(([field, value]) => {
    const from = value?.from ?? 'empty';
    const to = value?.to ?? 'empty';
    return `${field}: ${from} -> ${to}`;
  });
};

const timeAgo = (value) => {
  const diffMs = Date.now() - new Date(value).getTime();
  const diffMinutes = Math.max(Math.floor(diffMs / 60000), 0);

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes === 1) return '1 min ago';
  if (diffMinutes < 60) return `${diffMinutes} mins ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;

  const diffDays = Math.floor(diffHours / 24);
  return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
};

const getActivityTone = (activity) => {
  if (activity.action === 'created') {
    return {
      container: 'bg-amber-50 text-amber-700',
      icon: UserPlusIcon
    };
  }

  if (activity.action === 'updated' || activity.action === 'merge_manual' || activity.action === 'merge_import') {
    return {
      container: 'bg-sky-50 text-sky-700',
      icon: PencilSquareIcon
    };
  }

  if (activity.action === 'status_hired') {
    return {
      container: 'bg-emerald-50 text-emerald-700',
      icon: CheckCircleIcon
    };
  }

  if (activity.action?.startsWith('status_')) {
    return {
      container: 'bg-violet-50 text-violet-700',
      icon: BriefcaseIcon
    };
  }

  if (activity.action?.startsWith('email_')) {
    return {
      container: activity.action === 'email_failed_auto' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700',
      icon: EnvelopeIcon
    };
  }

  return {
    container: 'bg-slate-100 text-slate-700',
    icon: DocumentTextIcon
  };
};

const ActivityLog = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activities, setActivities] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [actionOptions, setActionOptions] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('all');
  const [selectedAction, setSelectedAction] = useState('all');
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  const fetchActivity = useCallback(async ({ offset = 0, append = false, memberId = selectedMemberId, action = selectedAction } = {}) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError('');
    }

    try {
      const response = await axios.get('/api/activity', {
        params: {
          limit: 20,
          offset,
          teamMemberId: memberId,
          action
        }
      });

      const nextActivities = response.data.activities || [];
      setActivities((prev) => (append ? [...prev, ...nextActivities] : nextActivities));
      setTeamMembers((response.data.teamMembers || []).filter((member) => !member.isSuperAdmin));
      setActionOptions(response.data.filters?.actionOptions || []);
      setHasMore(Boolean(response.data.pagination?.hasMore));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load activity log');
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [selectedAction, selectedMemberId]);

  useEffect(() => {
    fetchActivity({ memberId: selectedMemberId, action: selectedAction });
  }, [fetchActivity, selectedMemberId, selectedAction]);

  useEffect(() => {
    if (loading || loadingMore || !hasMore || !sentinelRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          fetchActivity({
            offset: activities.length,
            append: true,
            memberId: selectedMemberId,
            action: selectedAction
          });
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [activities.length, fetchActivity, hasMore, loading, loadingMore, selectedMemberId, selectedAction]);

  const emptyLabel = useMemo(() => {
    const selectedOption = actionOptions.find((option) => option.value === selectedAction);
    if (selectedAction === 'all') {
      return 'No recent activity yet.';
    }

    return `No activity found for ${selectedOption?.label?.toLowerCase() || 'this filter'}.`;
  }, [actionOptions, selectedAction]);

  return (
    <Layout>
      <div className="space-y-6 px-4 py-6 sm:px-0">
        <div className="flex flex-col gap-4 rounded-[28px] border border-gray-200 bg-white px-6 py-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-gray-900">Recent Activity</h1>
            <p className="mt-1 text-sm text-gray-500">
              The latest hiring activity across the team, with more events loading as you scroll.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 lg:justify-end">
            <select
              value={selectedMemberId}
              onChange={(event) => setSelectedMemberId(event.target.value)}
              className="h-10 min-w-[150px] rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              aria-label="Filter activity by team member"
            >
              <option value="all">Everyone</option>
              {teamMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>

            <select
              value={selectedAction}
              onChange={(event) => setSelectedAction(event.target.value)}
              className="h-10 min-w-[180px] rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              aria-label="Filter activity by action"
            >
              {actionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="rounded-[28px] border border-gray-200 bg-white px-6 py-10 text-center text-sm text-gray-500 shadow-sm">
            Loading activity...
          </div>
        ) : error ? (
          <div className="rounded-[28px] border border-red-200 bg-red-50 px-6 py-4 text-sm text-red-700 shadow-sm">
            {error}
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => {
              const tone = getActivityTone(activity);
              const ToneIcon = tone.icon;
              const changedFields = formatChangedFields(activity.changedFields);

              return (
                <article
                  key={activity.id}
                  className="rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-sm transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone.container}`}>
                      <ToneIcon className="h-5 w-5" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {activity.user?.name || 'System'}
                          </p>
                          <p className="mt-1 text-sm font-semibold text-gray-900">
                            {activity.applicant?.name || 'Applicant'}
                            {activity.applicant?.position ? ` • ${activity.applicant.position}` : ''}
                          </p>
                        </div>
                        <span className="shrink-0 whitespace-nowrap text-[11px] text-gray-500">
                          {timeAgo(activity.createdAt)}
                        </span>
                      </div>

                      <p className="mt-1 text-sm font-medium text-gray-800">{activity.actionLabel}</p>

                      {activity.details && (
                        <p className="mt-1 text-xs leading-5 text-gray-500">
                          {activity.details}
                        </p>
                      )}

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {activity.applicant?.status && (
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClasses[activity.applicant.status] || 'bg-gray-100 text-gray-700'}`}>
                            {formatStatus(activity.applicant.status)}
                          </span>
                        )}

                        {changedFields.slice(0, 3).map((item) => (
                          <span
                            key={item}
                            className="inline-flex rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-600"
                          >
                            {item}
                          </span>
                        ))}

                        {changedFields.length > 3 && (
                          <span className="inline-flex rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-600">
                            +{changedFields.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}

            {activities.length === 0 && (
              <div className="rounded-[28px] border border-dashed border-gray-200 bg-white px-6 py-10 text-sm text-gray-500 shadow-sm">
                {emptyLabel}
              </div>
            )}
          </div>
        )}

        {!loading && !error && hasMore && <div ref={sentinelRef} className="h-10" />}

        {!loading && !error && loadingMore && (
          <div className="rounded-2xl border border-gray-200 bg-white p-4 text-center text-sm text-gray-500 shadow-sm">
            Loading more activity...
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ActivityLog;
